import { memo, useMemo } from "react";
import { LineChart, ResponsiveContainer, XAxis, YAxis, Line, CartesianGrid, Tooltip } from "recharts";
import type { IrrData } from "./types";
import { getIrrValue } from "./utils";

/** Memoized table row to avoid re-rendering all rows when only excludeInput changes. */
const IRRTableRow = memo(function IRRTableRow({
  root,
  d,
  isExcluded,
  sofrAdjusted,
}: {
  root: string;
  d: IrrData[string];
  isExcluded: boolean;
  sofrAdjusted: boolean;
}) {
  const pts = d.data[d.active_spread] ?? [];
  const last = pts[pts.length - 1];
  const irrVal = last ? getIrrValue(last as { irr: number; irr_sofr_adjusted?: number }, sofrAdjusted) : null;
  const chartData = useMemo(
    () =>
      pts.map((p) => {
        const pt = p as { date: string; irr: number; irr_sofr_adjusted?: number };
        return { ...pt, dateMs: new Date(pt.date).getTime(), irr: getIrrValue(pt, sofrAdjusted) };
      }),
    [d.data, d.active_spread, sofrAdjusted]
  );
  return (
    <tr
      className={`border-bottom${isExcluded ? " table-secondary disabled-row" : ""}`}
      style={isExcluded ? { opacity: 0.5, pointerEvents: "none", backgroundColor: "#e9ecef" } : undefined}
    >
      <td className="text-nowrap fw-medium py-2 ps-4 px-3">{root}</td>
      <td className="text-nowrap fw-medium py-2 px-3">{d.name}</td>
      <td className="text-muted small py-2 px-3">{last?.date ?? "—"}</td>
      <td className="text-nowrap py-2 px-3">{irrVal !== null ? (irrVal * 100).toFixed(3) + "%" : "—"}</td>
      <td className="py-1 ps-1 pe-4" style={{ width: 200, minHeight: 75 }}>
        <div style={{ height: 75 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid stroke="#e3e3e3" strokeDasharray="2 5" />
              <XAxis
                dataKey="dateMs"
                type="number"
                scale="time"
                domain={["dataMin", "dataMax"]}
                hide={false}
                tickFormatter={(t) => new Date(t).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
              />
              <YAxis hide={true} tick={{ fontSize: 10 }} domain={["dataMin", "dataMax"]} />
              <Line
                type="linear"
                dataKey="irr"
                stroke="#0d6efd"
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
                connectNulls={true}
              />
              <Tooltip
                labelFormatter={(label: number) =>
                  new Date(label).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })
                }
                contentStyle={{ fontSize: 11, padding: "1px 2px" }}
                itemStyle={{ fontSize: 11, padding: "0 0 0px 0" }}
                labelStyle={{ fontSize: 11, padding: "0 0 0 0" }}
                formatter={(value: number, name: string) => [
                  typeof value === "number" ? (100 * value).toFixed(3) + "%" : value,
                  name,
                ]}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </td>
    </tr>
  );
});

/** Memoized table so it doesn't re-render when only excludeInput changes. */
export const IRRLatestTable = memo(function IRRLatestTable({
  irrApiData,
  allExcludedRoots,
  sofrAdjusted,
}: {
  irrApiData: IrrData;
  allExcludedRoots: string[];
  sofrAdjusted: boolean;
}) {
  return (
    <table className="table table-hover mb-0 align-middle" style={{ minWidth: 650 }}>
      <thead className="table-light sticky-top">
        <tr>
          <th className="text-nowrap fw-semibold py-2 px-3" style={{ minWidth: "3rem" }}>
            Root
          </th>
          <th className="text-nowrap fw-semibold py-2 px-3">Name</th>
          <th className="text-nowrap fw-semibold py-2 px-3">Date</th>
          <th className="text-nowrap fw-semibold py-2 px-3">IRR</th>
          <th className="text-nowrap fw-semibold py-2 px-3" style={{ width: 200 }}>
            Chart
          </th>
        </tr>
      </thead>
      <tbody>
        {Object.entries(irrApiData).map(([root, d]) => (
          <IRRTableRow
            key={root}
            root={root}
            d={d}
            isExcluded={allExcludedRoots.includes(root)}
            sofrAdjusted={sofrAdjusted}
          />
        ))}
      </tbody>
    </table>
  );
});
